// --------------------------------------------------------------
// Created On: 2025-05-07
// Author: Hannah Vaughan
//
// Last Modified: 2026-06-02
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import styles from "./DigitalSensor.module.scss";
import { SCADA_RTU_DEVICE_TYPE } from "src/constants/miscellaneous";
import { DIGITAL_INPUTS } from "src/constants/attributes";

// Component that lets the user select a digital sensor's position, sensor type, and reported attributes.
export default function DigitalSensor(props: Props): Component {
  const digitalSensorId = useMemo(
    () => getDigitalSensorId(props.sourceStateAttribute, props.digitalSensorMap),
    [props.sourceStateAttribute, JSON.stringify(props.digitalSensorMap)]
  );
  const digitalSensorApplications = useMemo(
    () => getDigitalSensorApplications(props.sourceStateAttribute, props.digitalSensorMap, props.digitalSensors),
    [props.sourceStateAttribute, JSON.stringify(props.digitalSensorMap), JSON.stringify(props.digitalSensors)]
  );
  const digitalSensorReportingDescription = useMemo(
    () => getSelectedDigitalSensorReportingDescription(props.sourceStateAttribute, props.digitalSensorMap),
    [props.sourceStateAttribute, JSON.stringify(props.digitalSensorMap)]
  );

  const availableDigitalSensors = useMemo(
    () => getAvailableDigitalSensors(props.deviceType, props.digitalSensors, props.sensorNumber),
    [props.deviceType, JSON.stringify(props.digitalSensors), props.sensorNumber]
  );

  // Returns the available digital sensors when configuring an asset
  function getAvailableDigitalSensors(
    deviceType: string | null,
    digitalSensors: DigitalSensor[],
    sensorNumber: number
  ): DigitalSensor[] {
    // The SCADA RTU only supports counters on digital sensor for ports 1-4 so if the device type is an RTU filter the
    // available sensors based on which sensorNumber we are configuring.
    if (deviceType === SCADA_RTU_DEVICE_TYPE) {
      return digitalSensors.filter((digitalSensor) => {
        if (sensorNumber <= 4) {
          return digitalSensor.hasCounterSupport;
        } else {
          return !digitalSensor.hasCounterSupport;
        }
      });
    } else {
      return digitalSensors;
    }
  }

  // Returns a digital sensor ID if it exists, otherwise an empty string.
  function getDigitalSensorId(attributeCode: string, digitalSensorMap: DigitalSensorMap): number | null {
    if (
      digitalSensorMap[attributeCode] !== undefined &&
      digitalSensorMap[attributeCode] !== null &&
      digitalSensorMap[attributeCode].digitalSensorId !== undefined
    ) {
      return digitalSensorMap[attributeCode].digitalSensorId;
    } else {
      return null;
    }
  }

  // Returns a list of all of the applications supported by a selected digital sensor.
  function getDigitalSensorApplications(
    attributeCode: string,
    digitalSensorMap: DigitalSensorMap,
    digitalSensors: DigitalSensor[]
  ): DigitalApplication[] {
    if (
      digitalSensorMap[attributeCode] !== undefined &&
      digitalSensorMap[attributeCode] !== null &&
      digitalSensorMap[attributeCode].digitalSensorId !== undefined
    ) {
      const selectedSensor = digitalSensors.find(
        (digitalSensor) => digitalSensorMap[attributeCode].digitalSensorId === digitalSensor.digitalSensorId
      );
      if (selectedSensor !== undefined) {
        return selectedSensor.applications;
      }
    }

    return [];
  }

  // Returns a digital sensor's currently selected reporting description, otherwise an empty string.
  function getSelectedDigitalSensorReportingDescription(
    attributeCode: string,
    digitalSensorMap: DigitalSensorMap
  ): string {
    if (
      digitalSensorMap[attributeCode] !== undefined &&
      digitalSensorMap[attributeCode] !== null &&
      digitalSensorMap[attributeCode].reportingDescription !== undefined
    ) {
      return String(digitalSensorMap[attributeCode].reportingDescription);
    } else {
      return "";
    }
  }

  return (
    <div className="row align-items-center mx-1">
      <div className="col g-0">
        <div data-test="digital-sensor-container" className="container mt-2 py-0">
          <div className={`${styles.body} row p-0 m-0`}>
            <div className="col py-2 pe-2">
              <div className="row align-items-center my-2 my-lg-1">
                {/* Dropdown for selecting sensor position. */}
                <div className="col-auto my-2 my-lg-1">
                  <label className={styles.title}>Sensor Position</label>
                  <select
                    data-test="digital-sensor-number-selection"
                    className="form-select"
                    value={props.sensorNumber}
                    onChange={(e) =>
                      props.onChange(
                        props.formId,
                        parseInt(e.target.value, 10),
                        // For SCADA RTU devices we want to clear the currently selected digital sensor when the sensor
                        // number changes because only certain ports support certain sensors.
                        props.deviceType === SCADA_RTU_DEVICE_TYPE ? null : digitalSensorId,
                        DIGITAL_INPUTS[parseInt(e.target.value, 10) - 1].stateAttribute,
                        digitalSensorReportingDescription
                      )
                    }
                    disabled={props.disabled}
                  >
                    <option value="-1">Select...</option>
                    {props.sensorPositions.map((sensorPosition) => (
                      <option
                        key={sensorPosition}
                        value={sensorPosition}
                        disabled={
                          (props.digitalSensorsInUse !== null &&
                            props.digitalSensorsInUse !== undefined &&
                            Object.keys(props.digitalSensorsInUse).includes(String(sensorPosition))) ||
                          props.localSensorPositionsInUse.includes(sensorPosition)
                        }
                      >
                        #{sensorPosition}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dropdown for selecting sensor type. */}
                <div className="col my-2 my-lg-1">
                  <label className={styles.title}>Type of Sensor</label>
                  {props.sensorNumber > 0 ? (
                    <select
                      data-test="digital-sensor-type-selection"
                      className="form-select"
                      value={digitalSensorId ?? ""}
                      onChange={(e) =>
                        props.onChange(
                          props.formId,
                          props.sensorNumber,
                          parseInt(e.target.value, 10),
                          props.sourceStateAttribute,
                          digitalSensorReportingDescription
                        )
                      }
                      disabled={props.disabled}
                    >
                      <option value="">Select a sensor type...</option>
                      {availableDigitalSensors.length > 0 &&
                        availableDigitalSensors.map((digitalSensor) => (
                          <option key={digitalSensor.digitalSensorId} value={digitalSensor.digitalSensorId}>
                            {digitalSensor.name}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <input type="text" className="form-control" value="Select a sensor type..." disabled />
                  )}
                </div>

                {/* Dropdown for selecting sensor application. */}
                <div className="col my-2 my-lg-1">
                  <label className={styles.title}>Sensor Reports</label>
                  {digitalSensorApplications.length > 0 ? (
                    <select
                      data-test="digital-sensor-detail-selection"
                      className="form-select"
                      value={digitalSensorReportingDescription}
                      onChange={(e) =>
                        props.onChange(
                          props.formId,
                          props.sensorNumber,
                          digitalSensorId,
                          props.sourceStateAttribute,
                          e.target.value
                        )
                      }
                      disabled={props.disabled}
                    >
                      <option value="">Select a reporting description...</option>
                      {digitalSensorApplications.map((attribute) => (
                        <option key={attribute.reportingDescription} value={attribute.reportingDescription}>
                          {attribute.reportingDescription}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" className="form-control" value="Select a reporting description..." disabled />
                  )}
                </div>

                <div className="col-auto">
                  <button
                    data-test="delete-digital-sensor-button"
                    type="button"
                    className={`${styles.button} btn btn-danger float-end`}
                    onClick={() => props.onDelete(props.formId, props.sensorNumber, props.sourceStateAttribute)}
                    disabled={props.disabled}
                  >
                    <i className="fa fa-fw fa-times fa-xs" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  formIndex: number;
  formId: number;
  isRented: boolean;
  sensorNumber: number;
  sourceStateAttribute: string;
  sensorPositions: number[];
  digitalSensors: DigitalSensor[];
  digitalSensorMap: DigitalSensorMap;
  digitalSensorsInUse: SensorInfoMap | null;
  localSensorPositionsInUse: number[];
  deviceType: string | null;
  disabled: boolean;
  onCreate: () => void;
  onChange: (
    formId: number,
    sensorNumber: number,
    digitalSensorId: number | null,
    attributeStateCode: string,
    reportingDescription: string
  ) => void;
  onDelete: (formId: number, sensorNumber: number, attributeStateCode: string) => void;
}

interface DigitalSensor {
  digitalSensorId: number;
  name: string;
  applications: DigitalApplication[];
  hasCounterSupport: boolean;
}

interface DigitalApplication {
  attributeStateId: number;
  attributeFlowId: number;
  attributeAccumulationId: number;
  reportingDescription: string;
}

interface DigitalSensorMap {
  [key: string]: DigitalSensorSelection;
}

interface DigitalSensorSelection {
  digitalSensorId: number;
  attributeStateCode: string;
  attributeFlowCode: string;
  attributeAccumulationCode: string;
  attributeModeCode: string;
  attributeKFactorCode: string;
  attributeOffsetCode: string;
  attributeDebounceCode: string;
  attributeUnitsPerPulseCode: string;
  mappedAttributeStateId: number;
  mappedAttributeFlowId: number;
  mappedAttributeAccumulationId: number;
  reportingDescription: string;
}

interface SensorInfoMap {
  [sensorNumber: number]: SensorInfo;
}

interface SensorInfo {
  reporting: string;
  assetName: string;
}
